import { useEffect, useRef, useState } from 'react';
import { useGeofenceStore } from '@/stores/geofenceStore';
import { useMyGeofences } from './useMyGeofences';
import {
  matchGeofences,
  sameMatches,
  EMPTY_MATCH_STATE,
  type GeofenceMatch,
} from '@/lib/geofence';

export type GeofencePermission = 'idle' | 'active' | 'denied' | 'unavailable';

/**
 * Foreground-bevakning för "Nyckel redo": watchPosition körs ENBART när
 * funktionen är på-slagen och fliken är synlig (Page Visibility API stoppar
 * bevakningen när appen bakgrundas — det är hela batteristrategin; ingen
 * bakgrundsgeofencing finns eller antyds). Positionen hålls enbart i minnet
 * här i callbacken — den skickas aldrig till servern och loggas aldrig.
 */
export function useGeofenceWatch() {
  const nearbyKeysEnabled = useGeofenceStore((s) => s.nearbyKeysEnabled);
  const { data: fences } = useMyGeofences();

  const [matches, setMatches] = useState<GeofenceMatch[]>([]);
  const [permission, setPermission] = useState<GeofencePermission>('idle');
  const [geoError, setGeoError] = useState<string | null>(null);

  // Matchtillståndet (dwell/hysteres) lever i en ref så att varje sample
  // inte orsakar en render — setMatches kallas bara när enhetsuppsättningen
  // faktiskt ändras.
  const stateRef = useRef(EMPTY_MATCH_STATE);
  const fencesRef = useRef(fences);
  fencesRef.current = fences;

  useEffect(() => {
    if (!nearbyKeysEnabled) {
      stateRef.current = EMPTY_MATCH_STATE;
      setMatches([]);
      setPermission('idle');
      setGeoError(null);
      return;
    }

    if (!('geolocation' in navigator)) {
      setPermission('unavailable');
      setGeoError('Geolocation stöds inte av din enhet');
      return;
    }

    let watchId: number | null = null;

    const stop = () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
        watchId = null;
      }
    };

    const start = () => {
      if (watchId !== null) return;
      watchId = navigator.geolocation.watchPosition(
        (pos) => {
          setPermission('active');
          setGeoError(null);
          const next = matchGeofences(
            { lat: pos.coords.latitude, lon: pos.coords.longitude },
            fencesRef.current ?? [],
            stateRef.current
          );
          const changed = !sameMatches(stateRef.current.matched, next.matched);
          stateRef.current = next;
          if (changed) setMatches(next.matched);
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) {
            // iOS Safari kan glömma beslutet mellan sessioner — flaggan
            // lämnas på så att en senare beviljad permission plockas upp,
            // men bevakningen stoppas utan om-prompt-loop.
            setPermission('denied');
            setGeoError('Platsåtkomst nekad. Tillåt platsåtkomst i inställningarna.');
            stop();
          } else if (err.code === err.POSITION_UNAVAILABLE) {
            setGeoError('Din position kunde inte bestämmas');
          } else if (err.code === err.TIMEOUT) {
            setGeoError('Tidsgräns nådd vid positionsbestämning');
          } else {
            setGeoError('Ett oväntat fel uppstod');
          }
        },
        { enableHighAccuracy: true, timeout: 20000, maximumAge: 15000 }
      );
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };

    document.addEventListener('visibilitychange', onVisibility);
    onVisibility();

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, [nearbyKeysEnabled]);

  return { matches, permission, geoError };
}
